"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.documentCaptionLayout = documentCaptionLayout;
exports.documentCaptionWarnings = documentCaptionWarnings;
const frame_layout_1 = require("./frame-layout");
/**
 * Caption layout — the derivation from stored transcripts to on-screen phrases. Pure arithmetic (no
 * React, no LLM), sibling of `frame-layout.ts` and equally THE authority: the Player preview, the
 * approval render, and the caption lints all call the two functions below and never re-derive
 * anything themselves, so they cannot disagree.
 *
 * All intervals are half-open `[fromFrame, toFrame)` in integer frames. The rounding discipline
 * matters more than it looks: a word's layer-local frame is the DIFFERENCE of two independently
 * rounded values (`sourceFrame(word) − sourceFrame(sourceIn)`), never a single rounding of the
 * subtracted seconds — the two forms disagree by ±1 near half-frame boundaries, and that is the
 * exact bug class two prior frame regressions came from (run-10 endAt rounding, Stage-4 playFrames).
 */
/** One conversion function, used everywhere seconds become frames — see the module doc above. */
function sourceFrame(seconds, fps) {
    return Math.round(seconds * fps);
}
/**
 * Resolve every transcript word on one layer to absolute frames, per design D2 §2b-§2c.
 *
 * `windowFrom` is the layer's own window start in its container's frames (`round(appearsAt · fps)`);
 * `base` is where that window lands on the ABSOLUTE document clock (`sequences[i].from + windowFrom`
 * for a scene layer, `windowFrom` itself for an overlay — see the two call sites below);
 * `containerFrames` is the scene's `durationInFrames` or the document's `totalFrames`.
 */
function evaluateLayerWords(layer, fps, windowFrom, base, containerFrames) {
    const transcript = layer.transcript;
    if (!transcript)
        return [];
    const sourceInFrame = sourceFrame(layer.sourceIn ?? 0, fps);
    // Only a video layer's schema carries sourceOut — an audio layer can only trim its front.
    const sourceOutSeconds = layer.type === 'video' ? layer.sourceOut : undefined;
    const sourceOutFrame = sourceOutSeconds != null ? sourceFrame(sourceOutSeconds, fps) : undefined;
    // Mirrors the composition's resolveLayerWindow — with one deliberate difference: NO floor on
    // `remaining`. The composition can afford its floor because a parent <Sequence> clips a layer
    // whose window starts at/past the container's end into invisibility; captions have no parent
    // clamp, so flooring here would emit a 1-frame phantom phrase (possibly past totalFrames) for
    // speech that never plays. remaining <= 0 must exclude every word instead.
    const windowLen = layer.endsAt != null ? Math.max(1, sourceFrame(layer.endsAt, fps) - windowFrom) : undefined;
    const remaining = containerFrames - windowFrom;
    const playFrames = windowLen != null ? Math.min(windowLen, remaining) : remaining;
    const playableEnd = base + playFrames;
    return transcript.words.map((word) => {
        const wordStartFrame = sourceFrame(word.startSeconds, fps);
        // Difference of independently rounded values — see the module doc's rounding discipline.
        const localStart = wordStartFrame - sourceInFrame;
        if (localStart < 0)
            return { status: 'trimmed' }; // trimmed off the front
        if (sourceOutFrame != null && wordStartFrame >= sourceOutFrame)
            return { status: 'trimmed' }; // trimmed off the back
        const fromFrame = base + localStart;
        if (fromFrame >= playableEnd)
            return { status: 'pastPlayableSpan' }; // outlives the layer's playable window
        const localEnd = sourceFrame(word.endSeconds, fps) - sourceInFrame;
        const toFrame = Math.min(Math.max(fromFrame + 1, base + localEnd), playableEnd); // a rounding-collapsed word keeps 1 frame
        return { status: 'rendered', word: { text: word.text, fromFrame, toFrame, sourceLayerId: layer.id } };
    });
}
/** The transcribable layers of a document, each resolved against its own frame of reference. */
function transcribedLayerWords(doc, fps) {
    const { sequences, totalFrames } = (0, frame_layout_1.documentFrameLayout)(doc, fps);
    const out = [];
    for (const { scene, from, durationInFrames } of sequences) {
        for (const layer of scene.layers) {
            if (layer.type !== 'video' && layer.type !== 'audio')
                continue;
            if (!layer.transcript)
                continue;
            const windowFrom = sourceFrame(layer.appearsAt ?? 0, fps);
            out.push({ layer, verdicts: evaluateLayerWords(layer, fps, windowFrom, from + windowFrom, durationInFrames) });
        }
    }
    for (const layer of doc.overlays ?? []) {
        if (layer.type !== 'video' && layer.type !== 'audio')
            continue;
        if (!layer.transcript)
            continue;
        const windowFrom = sourceFrame(layer.appearsAt ?? 0, fps);
        out.push({ layer, verdicts: evaluateLayerWords(layer, fps, windowFrom, windowFrom, totalFrames) });
    }
    return out;
}
/** Shared by every chunking mode: the inter-word gap past which speech is treated as real silence. */
const SILENCE_BREAK_SECONDS = 0.8;
/** Per-mode span/length caps for the `short`/`phrase` grouping rule (design D9) — `one-word` has no
 *  span/length cap of its own; it groups by the hold-to-next rule in {@link groupOneWordPhrases}. */
const CHUNKING_THRESHOLDS = {
    short: { maxSpanSeconds: 4, maxChars: 24 },
    phrase: { maxSpanSeconds: 4, maxChars: 42 },
};
/**
 * Group a time-ordered word stream into phrases, per design D2 §2d, parameterized by the document's
 * chunking mode. `short`/`phrase` share the Seedance segmenter's rule shape (break on silence, span,
 * or accumulated length — strict `>`, boundary values do not break) at the mode's thresholds;
 * `one-word` instead delegates to {@link groupOneWordPhrases}. Every mode obeys one editor-only rule:
 * a phrase never spans two source layers, so a dialogue clip and a voiceover can never stitch into
 * one Frankenstein phrase.
 */
function groupIntoPhrases(words, fps, chunking) {
    const silenceBreakFrames = SILENCE_BREAK_SECONDS * fps;
    if (chunking === 'one-word')
        return groupOneWordPhrases(words, silenceBreakFrames);
    const { maxSpanSeconds, maxChars } = CHUNKING_THRESHOLDS[chunking];
    const maxSpanFrames = maxSpanSeconds * fps;
    const phrases = [];
    let current = [];
    let currentChars = 0;
    const flush = () => {
        if (current.length === 0)
            return;
        // Max, not last: transcript words within one layer may overlap (agent-edited word timings —
        // Scribe never emits overlaps), and the phrase must contain every word's interval or a
        // mid-phrase word's toFrame escapes the phrase window handed to renderers and overlap
        // resolution. For non-overlapping words this IS the last word's toFrame.
        const phraseToFrame = Math.max(...current.map((word) => word.toFrame));
        phrases.push({
            fromFrame: current[0].fromFrame,
            toFrame: phraseToFrame,
            sourceLayerId: current[0].sourceLayerId,
            words: current.map((word, i) => ({
                text: word.text,
                fromFrame: word.fromFrame,
                toFrame: word.toFrame,
                // The Studio extend-to-next-word trick: a word stays highlighted until the next one
                // starts, so there is never an un-highlighted gap inside a phrase.
                highlightToFrame: i < current.length - 1 ? current[i + 1].fromFrame : phraseToFrame,
            })),
        });
        current = [];
        currentChars = 0;
    };
    for (const word of words) {
        const previous = current.at(-1);
        if (previous) {
            const sourceChanged = word.sourceLayerId !== previous.sourceLayerId;
            const silenceBreak = word.fromFrame - previous.toFrame > silenceBreakFrames;
            const spanBreak = word.toFrame - current[0].fromFrame > maxSpanFrames;
            const charsBreak = currentChars + 1 + word.text.length > maxChars;
            if (sourceChanged || silenceBreak || spanBreak || charsBreak)
                flush();
        }
        currentChars = current.length === 0 ? word.text.length : currentChars + 1 + word.text.length;
        current.push(word);
    }
    flush();
    return phrases;
}
/**
 * `chunking: 'one-word'` grouping (design D9/§2d): every surviving word becomes its own phrase. A
 * word holds on screen through an ordinary inter-word gap — it stays visible until the next word's
 * start when the next word exists, shares its source layer, and starts within
 * {@link SILENCE_BREAK_SECONDS} of this word's own end — the CapCut hold. Otherwise (a real silence,
 * a source change, or no next word) the phrase ends at the word's own `toFrame`. A phrase that would
 * collapse to zero length (two words rounding to the same frame) is dropped rather than emitted empty
 * — consistent with the pinned same-frame empty-highlight acceptance in the `short`/`phrase` grouper.
 */
function groupOneWordPhrases(words, silenceBreakFrames) {
    const phrases = [];
    for (let i = 0; i < words.length; i++) {
        const word = words[i];
        const next = words[i + 1];
        const holdsToNext = next != null && next.sourceLayerId === word.sourceLayerId && next.fromFrame - word.toFrame <= silenceBreakFrames;
        const toFrame = holdsToNext ? next.fromFrame : word.toFrame;
        if (toFrame <= word.fromFrame)
            continue; // same-frame words collapse the phrase to nothing
        phrases.push({
            fromFrame: word.fromFrame,
            toFrame,
            sourceLayerId: word.sourceLayerId,
            // The single word is the whole phrase, so it is highlighted for its entire duration.
            words: [{ text: word.text, fromFrame: word.fromFrame, toFrame, highlightToFrame: toFrame }],
        });
    }
    return phrases;
}
/**
 * Resolve crossfade overlap per design D2 §2e: scenes joined by a transition are co-mounted for the
 * overlap, so two adjacent phrases can legitimately share absolute frames. Exactly one must display
 * at any frame, so the earlier (outgoing) phrase is truncated at the later (arriving) one's start —
 * the arriving scene owns the ambiguous instant, mirroring Stage 4's time-anchor tiebreak. Phrases
 * are walked in `fromFrame` order (already true: words were merged in that order before grouping), so
 * only the immediate next phrase can possibly overlap.
 */
function resolveOverlaps(phrases) {
    const resolved = [];
    for (let i = 0; i < phrases.length; i++) {
        const phrase = phrases[i];
        const next = phrases[i + 1];
        if (!next || phrase.toFrame <= next.fromFrame) {
            resolved.push(phrase);
            continue;
        }
        // Words that start at or after the next phrase's arrival can't partially render — drop them;
        // the rest clamp their end (and highlight end) to exactly where the next phrase begins.
        const words = phrase.words
            .filter((word) => word.fromFrame < next.fromFrame)
            .map((word) => ({
            ...word,
            toFrame: Math.min(word.toFrame, next.fromFrame),
            highlightToFrame: Math.min(word.highlightToFrame, next.fromFrame),
        }));
        if (words.length === 0)
            continue; // truncated to nothing — drop the phrase entirely
        resolved.push({ ...phrase, toFrame: next.fromFrame, words });
    }
    return resolved;
}
/**
 * Derive display captions for a document at a given fps — THE single source every consumer (Player
 * preview, approval render, lints) must call; phrases are never cached or stored. See the module doc
 * for the rounding discipline and design D2 §2a-§2e for the normative formulas this implements.
 */
function documentCaptionLayout(doc, fps) {
    const rendered = [];
    for (const { verdicts } of transcribedLayerWords(doc, fps)) {
        for (const verdict of verdicts) {
            if (verdict.status === 'rendered')
                rendered.push(verdict.word);
        }
    }
    // Ties (equal fromFrame) keep document order — scenes by index then overlays, layers by array
    // order — because `transcribedLayerWords` visits them in exactly that order and Array#sort is
    // stable.
    rendered.sort((a, b) => a.fromFrame - b.fromFrame);
    const chunking = doc.captions?.style?.chunking ?? 'short';
    return { phrases: resolveOverlaps(groupIntoPhrases(rendered, fps, chunking)) };
}
/**
 * Caption-specific lints (design D7): hazards the deterministic layout can see but a plain schema
 * check cannot. Not the write-path entry point — folded into `documentWarnings` in `frame-layout.ts`,
 * which every write surface actually calls. Exported so it can be tested in isolation.
 */
function documentCaptionWarnings(doc) {
    const fps = doc.canvas.fps;
    const warnings = [];
    const transcribed = transcribedLayerWords(doc, fps);
    const totalWords = transcribed.reduce((sum, { verdicts }) => sum + verdicts.length, 0);
    if (doc.captions?.enabled && totalWords === 0) {
        warnings.push('Captions are enabled but no layer has a transcript — run generateCaptions to transcribe a source.');
    }
    for (const { layer, verdicts } of transcribed) {
        if (verdicts.some((v) => v.status === 'pastPlayableSpan')) {
            warnings.push(`Layer "${layer.id}": some transcript words fall past the layer's playable span and will not render as captions.`);
        }
    }
    for (const { layer, verdicts } of transcribed) {
        if (layer.volume === 0 && verdicts.length > 0) {
            warnings.push(`Layer "${layer.id}" has a transcript but its volume is 0 — captions will show for audio that is not audible.`);
        }
    }
    const spans = transcribed
        .map(({ layer, verdicts }) => {
        const rendered = verdicts.flatMap((v) => (v.status === 'rendered' ? [v.word] : []));
        if (rendered.length === 0)
            return undefined;
        return {
            layerId: layer.id,
            from: Math.min(...rendered.map((w) => w.fromFrame)),
            to: Math.max(...rendered.map((w) => w.toFrame)),
        };
    })
        .filter((span) => span != null);
    for (let i = 0; i < spans.length; i++) {
        for (let j = i + 1; j < spans.length; j++) {
            const a = spans[i];
            const b = spans[j];
            if (a.from < b.to && b.from < a.to) {
                warnings.push(`Layer "${a.layerId}" and layer "${b.layerId}" both have transcribed speech overlapping in time — captions will interleave. Caption one source instead.`);
            }
        }
    }
    return warnings;
}
